import type {
  StripeCheckoutBillingInterval,
  StripeCheckoutStatus
} from '@stripe/stripe-js';
import type { ENDPOINTS, INTEGRATION_STRIPE_SETTINGS_KEY } from './constants';
import type { PaymentCurrency } from './currencies';

export type StripeIntegration = {
  id: string;
  publishableKey: string;
  cancellationPolicyText: string;
};

export type StripeIntegrationSettings = {
  [INTEGRATION_STRIPE_SETTINGS_KEY]: StripeIntegration;
};

export const PAYMENT_SESSION_STATUSES = [
  'unset',
  'error',
  'complete',
  'expired',
  'open'
] as const;

export type PaymentSessionStatus = (typeof PAYMENT_SESSION_STATUSES)[number];

export function isPaymentSessionStatus(
  value: string
): value is PaymentSessionStatus {
  return PAYMENT_SESSION_STATUSES.includes(value as PaymentSessionStatus);
}

export type CustomerData = {
  email: string;
  name?: string;
};

export type PaymentProductData = {
  name: string;
  amount: number;
  currency: PaymentCurrency;
  quantity: number;
};

export type SubscriptionProductData = PaymentProductData & {
  recurringInterval: StripeCheckoutBillingInterval;
  recurringIntervalCount: number;
};

export function isSubscriptionData(
  productData: ProductData
): productData is SubscriptionProductData {
  const { recurringInterval, recurringIntervalCount } =
    productData as SubscriptionProductData;

  return recurringInterval && typeof recurringIntervalCount === 'number';
}

export type ProductData = PaymentProductData | SubscriptionProductData;

export type PaymentMetadata = {
  widgetId: string;
  paymentContext: string;
  [key: string]: string | number;
};

export type WalletType =
  | 'apple_pay'
  | 'google_pay'
  | 'samsung_pay'
  | 'link'
  | 'amex_express_checkout'
  | 'masterpass'
  | 'visa_checkout'
  | 'unknown';

export type SessionStatusData = {
  status: StripeCheckoutStatus['type'];
  amount: number;
  paymentId: string | null;
  paymentStatus:
    | Extract<StripeCheckoutStatus, { type: 'complete' }>['paymentStatus']
    | null;
  paymentMethodType: string | null;
  cardNumber: string | null;
  cardBrand: string | null;
  cardWallet: WalletType | null;
  chargeId: string | null;
  chargeStatus: string;
  chargeCreatedAt: number;
  receiptUrl: string;
  receiptNumber: string | null;
  invoiceId: string | null;
  invoiceNumber: string | null;
  invoicePaymentPageUrl: string | null;
  failureCode: string | null;
  failureMessage: string | null;
};

export type SuccessfulSessionStatusData = Omit<
  SessionStatusData,
  'status' | 'paymentStatus'
> & {
  status: 'complete';
  paymentStatus: 'paid';
};

export function isSuccessfulSessionStatusData(
  data: SessionStatusData
): data is SuccessfulSessionStatusData {
  const { status, paymentStatus, failureCode, failureMessage } = data;
  return (
    !failureCode &&
    !failureMessage &&
    status === 'complete' &&
    paymentStatus === 'paid'
  );
}

export type Endpoint = (typeof ENDPOINTS)[keyof typeof ENDPOINTS];

export type SuccessfulRequest<PayloadType> = {
  code: 200 | 201 | 204;
  message: string;
  payload: PayloadType;
};

export type FailedRequest = {
  code: 400;
  message: string;
  payload: Record<string, unknown>;
};

export type CancellableWidgetRequestArgs = {
  widgetToken?: string;
  abortSignal?: AbortSignal;
};

export type RequestResult<PayloadType> =
  | SuccessfulRequest<PayloadType>
  | FailedRequest;

export type ResponseProcessorArgs<PayloadType, ResultType> = {
  response: Response;
  handleSuccess: (payload: PayloadType) => ResultType;
  errorMessage?: string;
};

export type ResponseProcessor<PayloadType, ResultType> = (
  args: ResponseProcessorArgs<PayloadType, ResultType>
) => Promise<ResultType>;

export type FetchBaseArgs<PayloadType, ResultType> = {
  url: string | URL;
  handleSuccess: (payload: PayloadType) => ResultType;
  init?: RequestInit;
  errorMessage?: string;
  handleResponse?: ResponseProcessor<PayloadType, ResultType>;
};

export type PaymentSuccessHandler = (
  sessionStatusData: SuccessfulSessionStatusData
) => void | Promise<void>;

export type PaymentProcessingChangeHandler = (isInProcess: boolean) => void;
